import React, { useEffect, useState } from 'react';
import { IField } from './Summary.interface';
import { Link } from '@zurich-data-dsm-icons/action.link';
import {
  IPutData,
  ServicesData,
  generateCSV,
  handleResetFieldsBAsedOnExtractionType,
  resetFieldsValues,
  IExtractionResult,
  onClaimsEmailResponse,
  setFieldsValues,
  onExtractionSummaryResponse
} from 'app/utils';
import FormData from 'form-data';
import {
  SummaryField,
  GeneratingExtraction,
  DownloadCSVButton
} from 'app/components';
import {
  getBusinessArea,
  getCurrentFile,
  getService,
  useAppSelector,
  useAppDispatch,
  setHash,
  getSummaryEdited,
  setCurrentSummary,
  setSummaryEdited,
  getCurrentSummary,
  getRegenerateExtraction,
  setRegenerateExtraction,
  setGeneratingExtraction,
  getEmailChain,
  setCurrentFile,
  setHighlightCoords,
  getSelectedLanguage,
  setSelectedLanguage
} from 'app/redux';
import {
  getFields,
  getFileExtraction,
  updateExtraction,
  getEmailSummary
} from 'integration';
import styles from './Summary.module.scss';
import { useNavigate } from 'react-router-dom';
import { Button } from '@zurich-primitives/button';
import config from 'config';
import languages from 'data/mock/languages.json';
import { getTranslatedFields } from 'integration/extraction/api';
import { SERVICE_NAMES } from 'app/utils/consts';
import { capitalize, transformExtractionResult } from 'app/utils/functions';

export const Summary: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentService = useAppSelector(getService);
  const currentBusinessArea = useAppSelector(getBusinessArea);
  const currentFile = useAppSelector(getCurrentFile);
  const currentSummary = useAppSelector(getCurrentSummary);
  const isSummaryEdited = useAppSelector(getSummaryEdited);
  const regenerateExtraction = useAppSelector(getRegenerateExtraction);
  const currentEmailChain = useAppSelector(getEmailChain);
  const [originalExtraction, setOriginalExtraction] =
    useState<IExtractionResult>();
  const [rawFields, setRawFields] = useState<IField | undefined>(undefined);
  const [fields, setFields] = useState<
    Array<{ query: string; value?: string }>
  >([]);
  const [currentTitle, setCurrentTitle] = useState<string>('');
  const selectedLanguage = useAppSelector(getSelectedLanguage);
  const AVG_DURATION_IN_MS = 30000;

  const setSummary = (
    saveOriginal: boolean,
    newSummary: IExtractionResult | IPutData,
    newHash?: string | undefined
  ): void => {
    saveOriginal && setOriginalExtraction(newSummary as IExtractionResult);
    dispatch(setCurrentSummary(newSummary));
    newHash && dispatch(setHash(newHash));
  };

  const onFinally = (): void => {
    dispatch(setRegenerateExtraction(false));
    dispatch(setGeneratingExtraction(false));
  };

  const fetchFileExtraction = ({
    text,
    file,
    data,
    ignoreCache
  }: {
    text?: string;
    file?: File;
    data?: IField;
    ignoreCache?: boolean;
  }): void => {
    dispatch(setGeneratingExtraction(true));
    if (currentService === SERVICE_NAMES.CLAIMS_EMAIL_SUMMARY) {
      text &&
        getEmailSummary(String(currentBusinessArea), String(currentService), {
          text
        })
          .then((response) => {
            onClaimsEmailResponse(response, setSummary);
          })
          .catch(() => {
            resetFieldsValues();
          })
          .finally(() => {
            onFinally();
          });
    } else {
      const body = createFormData(file, data, ignoreCache);
      getFileExtraction(
        String(currentBusinessArea),
        String(currentService),
        body
      )
        .then((response) => {
          handleExtractionResponse(response);
        })
        .catch(() => {
          handleExtractionFailure();
        })
        .finally(() => {
          onFinally();
        });
    }
  };

  const createFormData = (
    file: File | undefined,
    data: IField | undefined,
    ignoreCache: boolean | undefined
  ): FormData => {
    const body = new FormData();
    switch (ServicesData[String(currentService)].ExtractionType) {
      case 'extraction':
        body.append('file', file, file?.name);
        body.append('data', JSON.stringify(data));
        body.append('ignoreCache', ignoreCache ?? false);
        if (currentService === SERVICE_NAMES.CALL_SUMMARY) {
          body.append('language', selectedLanguage);
        }
        break;
      case 'summary':
        body.append('file', file, { filename: file?.name });
        break;
    }
    return body;
  };

  const handleExtractionResponse = (response: any): void => {
    if (
      currentService === SERVICE_NAMES.CONTRACT_INSIGHTS ||
      currentService === SERVICE_NAMES.CLAIMS_INSIGHTS
    ) {
      if (response.coords !== undefined)
        dispatch(setHighlightCoords(response.coords));
    }
    onExtractionSummaryResponse(currentService, response, setSummary);
  };

  const handleExtractionFailure = (): void => {
    resetFieldsValues();
    dispatch(setCurrentFile(undefined));
    navigate(config.APP.PATH.LANDING);
  };

  const fetchIdExtractPro = ({ file }: { file?: File }): void => {
    dispatch(setGeneratingExtraction(true));
    const body = new FormData();
    body.append('file', file, { filename: file?.name });
    dispatch(setCurrentSummary(undefined));
    getFileExtraction(String(currentBusinessArea), String(currentService), body)
      .then((response) => {
        let currentSummary: IExtractionResult = {};

        for (const field in response.results) {
          const value = {
            [field]: {
              value: response.results[field].value
            }
          };
          currentSummary = { ...currentSummary, ...value };
        }
        dispatch(setCurrentSummary(currentSummary));
        setFields([]);
        for (const field in response.results) {
          setFields((prev) => [
            ...prev,
            {
              query: field,
              value: response.results[field].value
            }
          ]);
        }
        setOriginalExtraction(currentSummary);
      })
      .catch(() => {
        resetFieldsValues();
        dispatch(setCurrentFile(undefined));
        navigate(config.APP.PATH.LANDING);
      })
      .finally(() => {
        onFinally();
      });
  };

  const handleSaveSummary = (): void => {
    currentSummary &&
      updateExtraction(
        String(currentBusinessArea),
        String(currentService),
        currentSummary as IPutData
      );
    dispatch(setSummaryEdited(false));
  };

  const handleOpenAPI = (): void => {
    currentService !== undefined &&
      navigate(config.APP.PATH.API.replace(':api', currentService));
  };

  useEffect(() => {
    if (currentBusinessArea && currentService && fields.length === 0) {
      switch (ServicesData[String(currentService)].ExtractionType) {
        case 'extraction':
          if (currentService === SERVICE_NAMES.ID_EXTRACT_PRO) {
            break;
          }
          getFields(currentBusinessArea, currentService)
            .then((response) => {
              setRawFields(response.fields);
              for (const field in response.fields) {
                setFields((prev) => [
                  ...prev,
                  { query: response.fields[field].query }
                ]);
              }
            })
            .catch((error) => {
              console.error(error);
              dispatch(setCurrentFile(undefined));
              navigate(config.APP.PATH.LANDING);
            });
          break;
        case 'summary':
          setFields([{ query: 'Summary' }]);
          break;
        case 'email-summary':
          setFields([{ query: 'Language' }, { query: 'Summary' }]);
          break;
      }
    } else {
      navigate(config.APP.PATH.LANDING);
    }
    dispatch(setRegenerateExtraction(null));
  }, [currentBusinessArea, currentService]);

  useEffect(() => {
    if (currentService) {
      if (shouldFetchFileExtraction()) {
        fetchFileExtraction({
          file: currentFile,
          data: rawFields,
          ignoreCache:
            regenerateExtraction === null ? false : regenerateExtraction
        });
      } else if (shouldFetchIdExtractPro()) {
        fetchIdExtractPro({
          file: currentFile
        });
      } else if (shouldFetchSummaryOrEmailSummary()) {
        if (currentService === SERVICE_NAMES.CLAIMS_EMAIL_SUMMARY) {
          fetchFileExtraction({
            text: currentEmailChain
          });
        } else {
          fetchFileExtraction({ file: currentFile });
        }
      }
      handleResetFieldsBAsedOnExtractionType(
        currentService,
        currentFile,
        currentEmailChain
      );
    }
  }, [currentFile, rawFields, currentEmailChain, regenerateExtraction]);

  const shouldFetchFileExtraction = (): boolean | undefined => {
    return (
      currentFile &&
      rawFields &&
      ServicesData[String(currentService)].ExtractionType === 'extraction' &&
      currentService !== SERVICE_NAMES.ID_EXTRACT_PRO &&
      (regenerateExtraction || regenerateExtraction === null)
    );
  };

  const shouldFetchIdExtractPro = (): boolean | undefined => {
    return (
      currentFile &&
      currentService === SERVICE_NAMES.ID_EXTRACT_PRO &&
      (regenerateExtraction || regenerateExtraction === null)
    );
  };

  const shouldFetchSummaryOrEmailSummary = (): boolean => {
    return Boolean(
      (Boolean(currentFile) ||
        (currentService === SERVICE_NAMES.CLAIMS_EMAIL_SUMMARY &&
          currentEmailChain)) &&
        ['summary', 'email-summary'].includes(
          ServicesData[String(currentService)].ExtractionType
        ) &&
        (regenerateExtraction || regenerateExtraction === null)
    );
  };

  useEffect(() => {
    if (originalExtraction) {
      setFieldsValues(originalExtraction);
    } else {
      resetFieldsValues();
    }
  }, [originalExtraction]);

  const handleOnLanguageSelectorChange = (e: {
    target: { value: any };
  }): void => {
    const language = String(e.target.value).toLocaleLowerCase();
    dispatch(setSelectedLanguage(language));
    dispatch(setGeneratingExtraction(true));
    const results: Record<string, { value: string }> = {};
    if (currentSummary) {
      const data = currentSummary.data;
      Object.keys(data).forEach((item) => {
        results[
          (data as unknown as Record<string, { key: string; value: string }>)[
            item
          ].key
        ] = {
          value: (data as unknown as Record<string, { value: string }>)[item]
            .value
        };
      });

      dispatch(setCurrentSummary(undefined));

      getTranslatedFields(String(currentBusinessArea), String(currentService), {
        results,
        language
      })
        .then((response) => {
          let currentSummary: IExtractionResult = {};

          for (const field in response.results) {
            const value = {
              [field]: {
                value: response.results[field].value
              }
            };
            currentSummary = { ...currentSummary, ...value };
          }
          dispatch(setCurrentSummary(currentSummary));

          setOriginalExtraction(currentSummary);
        })
        .catch(() => {
          resetFieldsValues();
          dispatch(setCurrentFile(undefined));
          navigate(config.APP.PATH.LANDING);
        })
        .finally(() => {
          onFinally();
        });
    }
  };

  return (
    <div className={styles['summary-section']}>
      <div className={styles.header}>
        <div className={styles['hihglight-switch']}>
          <h3>
            {currentService
              ? ServicesData[currentService].SummaryTitle
              : 'Summary'}
          </h3>
          {currentService === SERVICE_NAMES.CALL_SUMMARY && (
            <select
              id="languageSelect"
              defaultValue={capitalize(selectedLanguage)}
              className={styles['select-element']}
              onChange={handleOnLanguageSelectorChange}
            >
              {languages.map((language) => (
                <option key={language.name} value={language.name}>
                  {language.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <Button onClick={handleOpenAPI} variant="tertiary">
          <Button.Icon>
            <Link />
          </Button.Icon>
          <Button.Label>Use our API version</Button.Label>
        </Button>
      </div>
      {fields.map((field) => (
        <SummaryField
          key={`field-${field.query}`}
          id={`field-${field.query}`}
          title={field.query}
          currentTitle={currentTitle}
          setCurrentTitle={setCurrentTitle}
        />
      ))}
      <div className={styles.buttons}>
        {isSummaryEdited &&
          currentService !== SERVICE_NAMES.FNOL_CAPTURING &&
          currentService !== SERVICE_NAMES.CALL_SUMMARY && (
            <Button
              className={styles['btn-csv']}
              onClick={handleSaveSummary}
              variant="secondary"
            >
              <Button.Label>Save changes</Button.Label>
            </Button>
          )}
        {currentService !== undefined &&
          ServicesData[currentService].ExtractionType === 'extraction' && (
            <DownloadCSVButton
              handleOnClick={() => {
                if (currentFile && currentSummary) {
                  if ((currentSummary as IPutData).data) {
                    generateCSV(
                      (currentSummary as IPutData).data,
                      currentFile.name
                    );
                  } else {
                    generateCSV(
                      transformExtractionResult(
                        currentSummary as IExtractionResult
                      ),
                      currentFile.name
                    );
                  }
                }
              }}
              isDisabled={originalExtraction === undefined}
            />
          )}
      </div>
      {(() => {
        if (
          currentService === SERVICE_NAMES.CONTRACT_INSIGHTS ||
          currentService === SERVICE_NAMES.CLAIMS_INSIGHTS
        ) {
          return <GeneratingExtraction processTime={AVG_DURATION_IN_MS} />;
        }
        return <GeneratingExtraction />;
      })()}
    </div>
  );
};
